import * as crypto from "crypto";
import { createRequire } from "module";
import { Readable } from "stream";
import type {
  StorageFile,
  StorageListInput,
  StorageListPage,
  StorageReadGrantInput,
  StorageStoreOptions,
  StorageVisibility,
  StoredFile,
  UploadGrantInput,
} from "@gencow/core";
import { DEFAULT_STORAGE_QUOTA, MAX_FILE_SIZE, StorageListUnsupportedError, formatBytes } from "@gencow/core";
import { createStorageReadGrant } from "./storage-read-grant.js";
import { createSystemFilesCatalog } from "./storage-system-files-catalog.js";
import type {
  AzureBlobContainerClientLike,
  AzureBlobRuntimeOptions,
  AzureBlobServiceClientLike,
  StorageOptions,
  StorageRuntimeAdapter,
  SystemFilesCatalog,
} from "./storage-types.js";
import { normalizeStorageVisibility } from "./storage-visibility.js";

const DIRECT_UPLOAD_TTL_MS = 15 * 60 * 1000;

type AzureBlobStorageOptions = StorageOptions &
  AzureBlobRuntimeOptions & {
    createContainerClient?: (input: AzureBlobRuntimeOptions) => AzureBlobContainerClientLike;
  };

type AzureBlobResolvedClients = {
  containerClient: AzureBlobContainerClientLike;
  serviceClient?: AzureBlobServiceClientLike;
};

type EncodedMetadata = {
  gencowfilename: string;
  gencowtype: string;
  gencowsize: string;
  gencowvisibility: StorageVisibility;
  gencowsha256: string;
};

type PendingUploadMeta = {
  filename: string;
  contentType: string;
  declaredSize: number;
  checksum?: string;
  expiresAt: string;
  state: "pending" | "received";
};

function assertFileSize(size: number): void {
  if (size <= MAX_FILE_SIZE) return;
  throw new Error(`File too large: ${formatBytes(size)} exceeds limit of ${formatBytes(MAX_FILE_SIZE)}`);
}

function normalizeStoreOptions(input: string | StorageStoreOptions | undefined): {
  filename?: string;
  visibility: StorageVisibility;
} {
  if (typeof input === "string") return { filename: input, visibility: "private" };
  return {
    filename: input?.filename,
    visibility: normalizeStorageVisibility(input?.visibility),
  };
}

function normalizePrefix(prefix: string): string {
  return prefix.trim().replace(/^\/+|\/+$/g, "");
}

function blobObjectKey(input: { prefix: string; appName: string; storageId: string }): string {
  const prefix = normalizePrefix(input.prefix);
  const base = `apps/${encodeURIComponent(input.appName)}/objects/${input.storageId}`;
  return prefix ? `${prefix}/${base}` : base;
}

function transformPrefix(input: { prefix: string; appName: string; storageId: string }): string {
  const prefix = normalizePrefix(input.prefix);
  const base = `apps/${encodeURIComponent(input.appName)}/transforms/${input.storageId}/`;
  return prefix ? `${prefix}/${base}` : base;
}

function uploadObjectKey(input: { prefix: string; appName: string; uploadId: string }): string {
  const prefix = normalizePrefix(input.prefix);
  const base = `apps/${encodeURIComponent(input.appName)}/staging/${input.uploadId}`;
  return prefix ? `${prefix}/${base}` : base;
}

function sha256(buffer: Buffer): string {
  return crypto.createHash("sha256").update(buffer).digest("hex");
}

function normalizeChecksum(checksum: string | undefined): string | undefined {
  if (!checksum) return undefined;
  const normalized = checksum.trim().toLowerCase();
  if (!/^[a-f0-9]{64}$/.test(normalized)) throw new Error("Invalid upload checksum");
  return normalized;
}

function normalizeUploadInput(input: UploadGrantInput): PendingUploadMeta {
  if (!input.filename || input.filename.length > 500) throw new Error("Invalid upload filename");
  if (!input.contentType || input.contentType.length > 200) throw new Error("Invalid upload content type");
  if (!Number.isFinite(input.size) || input.size < 0) throw new Error("Invalid upload size");
  assertFileSize(input.size);
  return {
    filename: input.filename,
    contentType: input.contentType,
    declaredSize: input.size,
    ...(input.checksum ? { checksum: normalizeChecksum(input.checksum) } : {}),
    expiresAt: new Date(Date.now() + DIRECT_UPLOAD_TTL_MS).toISOString(),
    state: "pending",
  };
}

function encodeMetadata(input: {
  filename: string;
  type: string;
  size: number;
  visibility: StorageVisibility;
  contentHash: string;
}): EncodedMetadata {
  return {
    gencowfilename: Buffer.from(input.filename, "utf-8").toString("base64url"),
    gencowtype: input.type,
    gencowsize: String(input.size),
    gencowvisibility: normalizeStorageVisibility(input.visibility),
    gencowsha256: input.contentHash,
  };
}

function encodeUploadMetadata(input: PendingUploadMeta): Record<string, string> {
  return {
    gencowuploadfilename: Buffer.from(input.filename, "utf-8").toString("base64url"),
    gencowuploadcontenttype: input.contentType,
    gencowuploadsize: String(input.declaredSize),
    gencowuploadexpiresat: input.expiresAt,
    gencowuploadstate: input.state,
    ...(input.checksum ? { gencowuploadchecksum: input.checksum } : {}),
  };
}

function buildDirectUploadHeaders(input: PendingUploadMeta): Record<string, string> {
  const metadata = encodeUploadMetadata({ ...input, state: "received" });
  const headers: Record<string, string> = {
    "Content-Type": input.contentType,
    "x-ms-blob-type": "BlockBlob",
    "x-ms-blob-content-type": input.contentType,
  };
  for (const [key, value] of Object.entries(metadata)) {
    headers[`x-ms-meta-${key}`] = value;
  }
  return headers;
}

function decodeFilename(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  try {
    return Buffer.from(value, "base64url").toString("utf-8") || fallback;
  } catch {
    return fallback;
  }
}

function decodeUploadMetadata(metadata: Record<string, string> | undefined): PendingUploadMeta {
  const raw = metadata ?? {};
  const filename = decodeFilename(raw.gencowuploadfilename, "");
  const declaredSize = Number(raw.gencowuploadsize);
  if (!filename || !raw.gencowuploadcontenttype || !Number.isFinite(declaredSize) || declaredSize < 0) {
    throw new Error("Upload grant not found");
  }
  const state = raw.gencowuploadstate === "received" ? "received" : "pending";
  if (!raw.gencowuploadexpiresat || Number.isNaN(Date.parse(raw.gencowuploadexpiresat))) {
    throw new Error("Upload grant not found");
  }
  return {
    filename,
    contentType: raw.gencowuploadcontenttype,
    declaredSize,
    ...(raw.gencowuploadchecksum ? { checksum: normalizeChecksum(raw.gencowuploadchecksum) } : {}),
    expiresAt: raw.gencowuploadexpiresat,
    state,
  };
}

function assertUploadId(uploadId: string): void {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uploadId)) {
    throw new Error("Invalid upload id");
  }
}

function assertUploadActive(meta: PendingUploadMeta): void {
  if (Date.parse(meta.expiresAt) <= Date.now()) throw new Error("Upload grant expired");
}

function toStorageFile(input: {
  id: string;
  fallbackName: string;
  fallbackType?: string;
  fallbackSize?: number;
  metadata?: Record<string, string>;
}): StorageFile {
  const metadata = input.metadata ?? {};
  return {
    id: input.id,
    name: decodeFilename(metadata.gencowfilename, input.fallbackName),
    size: Number(metadata.gencowsize ?? input.fallbackSize ?? 0),
    type: metadata.gencowtype || input.fallbackType || "application/octet-stream",
    visibility: normalizeStorageVisibility(metadata.gencowvisibility),
  };
}

async function downloadToBuffer(blob: {
  downloadToBuffer?: () => Promise<Buffer>;
  download?: () => Promise<{ readableStreamBody?: NodeJS.ReadableStream; blobBody?: Promise<Blob> }>;
}): Promise<Buffer> {
  if (blob.downloadToBuffer) return Buffer.from(await blob.downloadToBuffer());
  if (!blob.download) throw new Error("Azure blob client does not support download");
  const downloaded = await blob.download();
  if (downloaded.blobBody) {
    return Buffer.from(await (await downloaded.blobBody).arrayBuffer());
  }
  if (!downloaded.readableStreamBody) throw new Error("Azure blob download returned no body");
  const chunks: Buffer[] = [];
  for await (const chunk of Readable.from(downloaded.readableStreamBody)) {
    chunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk));
  }
  return Buffer.concat(chunks);
}

async function deleteBlobIfExists(blob: {
  deleteIfExists?: () => Promise<{ succeeded?: boolean }>;
  delete?: () => Promise<void>;
}): Promise<void> {
  if (blob.deleteIfExists) {
    await blob.deleteIfExists();
    return;
  }
  try {
    await blob.delete?.();
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    if (!message.includes("BlobNotFound")) throw error;
  }
}

async function recordStored(params: {
  catalog?: SystemFilesCatalog;
  meta: StorageFile;
  uploadedBy: string;
  objectKey: string;
  etag?: string;
  contentHash: string;
}): Promise<void> {
  if (!params.catalog) return;
  try {
    await params.catalog.recordStored({
      ...params.meta,
      uploadedBy: params.uploadedBy,
      storageBackend: "azure-blob",
      objectKey: params.objectKey,
      etag: params.etag,
      contentHash: params.contentHash,
    });
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    console.warn(`[storage] DB record failed for ${params.meta.id}: ${msg}`);
    throw new Error("Storage catalog record failed");
  }
}

function createBlobSasPermissions(): unknown {
  const require = createRequire(import.meta.url);
  const storageBlob = require("@azure/storage-blob") as {
    BlobSASPermissions: { parse(permissions: string): unknown };
  };
  return storageBlob.BlobSASPermissions.parse("cw");
}

function resolveAzureClients(options: AzureBlobStorageOptions): AzureBlobResolvedClients {
  if (options.containerClient) {
    return {
      containerClient: options.containerClient,
      ...(options.serviceClient ? { serviceClient: options.serviceClient } : {}),
    };
  }
  if (options.createContainerClient) {
    return {
      containerClient: options.createContainerClient(options),
      ...(options.serviceClient ? { serviceClient: options.serviceClient } : {}),
    };
  }
  const require = createRequire(import.meta.url);
  const storageBlob = require("@azure/storage-blob") as {
    BlobServiceClient: new (
      url: string,
      credential: unknown,
    ) => AzureBlobServiceClientLike;
  };
  const identity = require("@azure/identity") as {
    ClientSecretCredential: new (tenantId: string, clientId: string, clientSecret: string) => unknown;
    ManagedIdentityCredential: new (clientId?: string) => unknown;
  };
  const auth = options.auth ?? "managed_identity";
  const credential =
    auth === "client_secret"
      ? new identity.ClientSecretCredential(
          requireNonEmpty(options.tenantId, "GENCOW_PLATFORM_STORAGE_AZURE_TENANT_ID"),
          requireNonEmpty(options.clientId, "GENCOW_PLATFORM_STORAGE_AZURE_CLIENT_ID"),
          requireNonEmpty(options.clientSecret, "GENCOW_PLATFORM_STORAGE_AZURE_CLIENT_SECRET"),
        )
      : new identity.ManagedIdentityCredential(options.clientId);
  const serviceClient = new storageBlob.BlobServiceClient(
    options.endpoint.replace(/\/+$/, ""),
    credential,
  );
  return {
    serviceClient,
    containerClient: serviceClient.getContainerClient(options.containerName),
  };
}

function requireNonEmpty(value: string | undefined, key: string): string {
  if (value?.trim()) return value.trim();
  throw new Error(`${key} is required for Azure Blob client_secret auth`);
}

export function createAzureBlobStorage(options: AzureBlobStorageOptions): StorageRuntimeAdapter {
  if (!options.appName) throw new Error("Azure Blob storage requires appName");
  const { containerClient, serviceClient } = resolveAzureClients(options);
  const catalog = options.rawSql ? createSystemFilesCatalog(options.rawSql) : undefined;
  const quota = options.storageQuota ?? DEFAULT_STORAGE_QUOTA;

  async function assertCanStore(size: number): Promise<void> {
    assertFileSize(size);
    await catalog?.assertCanStore({ size, quotaBytes: quota });
  }

  async function storeBufferInternal(input: {
    buffer: Buffer;
    filename: string;
    type: string;
    visibility: StorageVisibility;
    uploadedBy: string;
  }): Promise<string> {
    await assertCanStore(input.buffer.length);
    const id = crypto.randomUUID();
    const objectKey = blobObjectKey({ prefix: options.prefix, appName: options.appName!, storageId: id });
    const contentHash = sha256(input.buffer);
    const metadata = encodeMetadata({
      filename: input.filename,
      type: input.type,
      size: input.buffer.length,
      visibility: input.visibility,
      contentHash,
    });
    const blob = containerClient.getBlockBlobClient(objectKey);
    const upload = await blob.uploadData(input.buffer, {
      blobHTTPHeaders: { blobContentType: input.type },
      metadata,
    });
    const meta: StorageFile = {
      id,
      name: input.filename,
      size: input.buffer.length,
      type: input.type,
      visibility: input.visibility,
    };
    try {
      await recordStored({
        catalog,
        meta,
        uploadedBy: input.uploadedBy,
        objectKey,
        etag: upload.etag,
        contentHash,
      });
    } catch (error) {
      await deleteBlobIfExists(blob).catch(() => {});
      throw error;
    }
    return id;
  }

  return {
    kind: "azure-blob",

    async store(file: File | Blob, filenameOrOptions?: string | StorageStoreOptions): Promise<string> {
      const storeOptions = normalizeStoreOptions(filenameOrOptions);
      const idName = crypto.randomUUID();
      const name = storeOptions.filename || (file instanceof File ? file.name : `${idName}.bin`);
      const type = file.type || "application/octet-stream";
      return storeBufferInternal({
        buffer: Buffer.from(await file.arrayBuffer()),
        filename: name,
        type,
        visibility: storeOptions.visibility,
        uploadedBy: "api",
      });
    },

    async storeBuffer(buffer, filename, type = "application/octet-stream", opts) {
      return storeBufferInternal({
        buffer,
        filename,
        type,
        visibility: normalizeStorageVisibility(opts?.visibility),
        uploadedBy: "api",
      });
    },

    getUrl(storageId: string): string {
      return `/api/storage/${storageId}`;
    },

    getPublicUrl(storageId: string): string {
      return `/api/storage/${storageId}`;
    },

    async createUploadGrant(input) {
      const meta = normalizeUploadInput(input);
      await catalog?.assertCanStore({ size: meta.declaredSize, quotaBytes: quota });
      const uploadId = crypto.randomUUID();
      const objectKey = uploadObjectKey({ prefix: options.prefix, appName: options.appName!, uploadId });
      const blob = containerClient.getBlockBlobClient(objectKey);
      await blob.uploadData(Buffer.alloc(0), {
        blobHTTPHeaders: { blobContentType: meta.contentType },
        metadata: encodeUploadMetadata(meta),
      });
      if (
        options.directUploadMode === "azure_sas" &&
        serviceClient?.getUserDelegationKey &&
        blob.generateUserDelegationSasUrl
      ) {
        try {
          const expiresOn = new Date(meta.expiresAt);
          const startsOn = new Date(Date.now() - 5 * 60 * 1000);
          const userDelegationKey = await serviceClient.getUserDelegationKey(startsOn, expiresOn);
          const url = await blob.generateUserDelegationSasUrl(
            { permissions: createBlobSasPermissions(), startsOn, expiresOn },
            userDelegationKey,
          );
          return {
            uploadId,
            url,
            method: "PUT",
            headers: buildDirectUploadHeaders(meta),
            expiresAt: meta.expiresAt,
          };
        } catch (error) {
          await deleteBlobIfExists(blob).catch(() => {});
          throw error;
        }
      }
      return {
        uploadId,
        url: `/api/storage/upload/${uploadId}`,
        method: "PUT",
        headers: { "Content-Type": meta.contentType },
        expiresAt: meta.expiresAt,
      };
    },

    async receiveUpload(uploadId, input) {
      assertUploadId(uploadId);
      const objectKey = uploadObjectKey({ prefix: options.prefix, appName: options.appName!, uploadId });
      const blob = containerClient.getBlockBlobClient(objectKey);
      const meta = decodeUploadMetadata((await blob.getProperties()).metadata);
      assertUploadActive(meta);
      if ((input.contentType ?? "") !== meta.contentType) throw new Error("Upload content type mismatch");
      const buffer = Buffer.from(await input.body.arrayBuffer());
      assertFileSize(buffer.length);
      await blob.uploadData(buffer, {
        blobHTTPHeaders: { blobContentType: meta.contentType },
        metadata: encodeUploadMetadata({ ...meta, state: "received" }),
      });
    },

    async finalizeUpload(input) {
      assertUploadId(input.uploadId);
      const objectKey = uploadObjectKey({
        prefix: options.prefix,
        appName: options.appName!,
        uploadId: input.uploadId,
      });
      const blob = containerClient.getBlockBlobClient(objectKey);
      const properties = await blob.getProperties();
      const pending = decodeUploadMetadata(properties.metadata);
      assertUploadActive(pending);
      if (pending.state !== "received") throw new Error("Upload body missing");
      if (properties.contentLength !== undefined && properties.contentLength !== pending.declaredSize) {
        throw new Error("Upload size mismatch");
      }
      if (properties.contentType && properties.contentType !== pending.contentType) {
        throw new Error("Upload content type mismatch");
      }
      const buffer = await downloadToBuffer(blob);
      if (buffer.length !== pending.declaredSize) throw new Error("Upload size mismatch");
      const actualChecksum = sha256(buffer);
      const expectedChecksum = normalizeChecksum(input.checksum) ?? pending.checksum;
      if (expectedChecksum && expectedChecksum !== actualChecksum)
        throw new Error("Upload checksum mismatch");
      try {
        const assetId = await storeBufferInternal({
          buffer,
          filename: pending.filename,
          type: pending.contentType,
          visibility: normalizeStorageVisibility(input.visibility),
          uploadedBy: "direct-upload",
        });
        return { assetId };
      } finally {
        await deleteBlobIfExists(blob).catch(() => {});
      }
    },

    async abortUpload(uploadId) {
      assertUploadId(uploadId);
      const objectKey = uploadObjectKey({ prefix: options.prefix, appName: options.appName!, uploadId });
      await deleteBlobIfExists(containerClient.getBlockBlobClient(objectKey));
    },

    async reconcileUploads(input) {
      if (!containerClient.listBlobsFlat) return { removed: 0 };
      const nowMs = input?.nowMs ?? Date.now();
      const maxAgeMs = input?.maxAgeMs ?? DIRECT_UPLOAD_TTL_MS;
      let removed = 0;
      const prefix = uploadObjectKey({ prefix: options.prefix, appName: options.appName!, uploadId: "" });
      for await (const item of containerClient.listBlobsFlat({ prefix })) {
        const blob =
          containerClient.getBlobClient?.(item.name) ?? containerClient.getBlockBlobClient(item.name);
        const meta = await blob
          .getProperties()
          .then((props) => decodeUploadMetadata(props.metadata))
          .catch(() => null);
        const expiresMs = meta ? Date.parse(meta.expiresAt) : 0;
        const expired = !meta || expiresMs <= nowMs || expiresMs + maxAgeMs <= nowMs;
        if (!expired) continue;
        await deleteBlobIfExists(blob);
        removed++;
      }
      return { removed };
    },

    async createReadGrant(storageId: string, input?: StorageReadGrantInput) {
      if (!options.readGrantSecret) throw new Error("Storage read grants are not configured");
      const meta = await this.getMeta(storageId);
      if (!meta) throw new Error("Storage object not found");
      return createStorageReadGrant({
        appName: options.appName!,
        storageId,
        secret: options.readGrantSecret,
        input,
        nowMs: options.readGrantNowMs?.(),
      });
    },

    async get(storageId: string): Promise<StoredFile | null> {
      const objectKey = blobObjectKey({ prefix: options.prefix, appName: options.appName!, storageId });
      const blob =
        containerClient.getBlobClient?.(objectKey) ?? containerClient.getBlockBlobClient(objectKey);
      let properties;
      try {
        properties = await blob.getProperties();
      } catch {
        return null;
      }
      const catalogMeta = await catalog?.getMeta(storageId).catch(() => null);
      const meta =
        catalogMeta ??
        toStorageFile({
          id: storageId,
          fallbackName: storageId,
          fallbackSize: properties.contentLength,
          fallbackType: properties.contentType,
          metadata: properties.metadata,
        });
      try {
        const buffer = await downloadToBuffer(blob);
        return { ...meta, buffer };
      } catch {
        return null;
      }
    },

    async getMeta(storageId: string): Promise<StorageFile | null> {
      const catalogMeta = await catalog?.getMeta(storageId).catch(() => null);
      if (catalogMeta) return catalogMeta;
      const objectKey = blobObjectKey({ prefix: options.prefix, appName: options.appName!, storageId });
      const blob =
        containerClient.getBlobClient?.(objectKey) ?? containerClient.getBlockBlobClient(objectKey);
      try {
        const properties = await blob.getProperties();
        return toStorageFile({
          id: storageId,
          fallbackName: storageId,
          fallbackSize: properties.contentLength,
          fallbackType: properties.contentType,
          metadata: properties.metadata,
        });
      } catch {
        return null;
      }
    },

    async setVisibility(storageId: string, visibility: StorageVisibility): Promise<void> {
      const objectKey = blobObjectKey({ prefix: options.prefix, appName: options.appName!, storageId });
      const blob =
        containerClient.getBlobClient?.(objectKey) ?? containerClient.getBlockBlobClient(objectKey);
      const properties = await blob.getProperties();
      const previousMetadata = properties.metadata ?? {};
      const nextVisibility = normalizeStorageVisibility(visibility);
      if (blob.setMetadata) {
        await blob.setMetadata({ ...previousMetadata, gencowvisibility: nextVisibility });
      }
      await catalog?.setVisibility(storageId, nextVisibility).catch(async (error) => {
        if (blob.setMetadata) await blob.setMetadata(previousMetadata).catch(() => {});
        throw error;
      });
    },

    async listMeta(input?: StorageListInput): Promise<StorageListPage> {
      if (!catalog) {
        throw new StorageListUnsupportedError(
          "listMeta is not supported on Azure Blob storage without a SQL catalog.",
        );
      }
      return catalog.listMeta(input);
    },

    async getTransform(storageId: string, cacheKey: string): Promise<StoredFile | null> {
      const objectKey = `${transformPrefix({ prefix: options.prefix, appName: options.appName!, storageId })}${cacheKey}`;
      const blob =
        containerClient.getBlobClient?.(objectKey) ?? containerClient.getBlockBlobClient(objectKey);
      try {
        const properties = await blob.getProperties();
        const buffer = await downloadToBuffer(blob);
        return {
          id: cacheKey,
          name: cacheKey,
          size: buffer.byteLength,
          type: properties.contentType || "application/octet-stream",
          visibility: "private",
          buffer,
        };
      } catch {
        return null;
      }
    },

    async storeTransform(
      storageId: string,
      cacheKey: string,
      input: { buffer: Buffer; type: string },
    ): Promise<void> {
      const objectKey = `${transformPrefix({ prefix: options.prefix, appName: options.appName!, storageId })}${cacheKey}`;
      await containerClient.getBlockBlobClient(objectKey).uploadData(input.buffer, {
        blobHTTPHeaders: { blobContentType: input.type },
        metadata: {
          gencowsourceid: storageId,
          gencowcachekey: Buffer.from(cacheKey, "utf-8").toString("base64url"),
        },
      });
    },

    async countTransforms(): Promise<number> {
      if (!containerClient.listBlobsFlat) return 0;
      const prefix = `${normalizePrefix(options.prefix) ? `${normalizePrefix(options.prefix)}/` : ""}apps/${encodeURIComponent(
        options.appName!,
      )}/transforms/`;
      let count = 0;
      for await (const item of containerClient.listBlobsFlat({ prefix })) {
        if (item.name) count++;
      }
      return count;
    },

    async delete(storageId: string): Promise<void> {
      const objectKey = blobObjectKey({ prefix: options.prefix, appName: options.appName!, storageId });
      await deleteBlobIfExists(
        containerClient.getBlobClient?.(objectKey) ?? containerClient.getBlockBlobClient(objectKey),
      );
      if (containerClient.listBlobsFlat) {
        for await (const item of containerClient.listBlobsFlat({
          prefix: transformPrefix({ prefix: options.prefix, appName: options.appName!, storageId }),
        })) {
          await deleteBlobIfExists(
            containerClient.getBlobClient?.(item.name) ?? containerClient.getBlockBlobClient(item.name),
          );
        }
      }
      await catalog?.remove(storageId);
    },
  };
}
